// SendToIntegrityButton — agent-facing control on the Lead Detail page.
//
// Flow:
//   1. Load the list of Integrity credentials this agent can push under
//      (usually one — their own NPN). Load the product taxonomy for the
//      dropdown.
//   2. If the lead already has an IntegrityLeadLink, show its Quick Quote
//      deep-link instead of the push button.
//   3. On click, POST /api/integrity/leads/:id/push with { npn, product }.
//      Success ⇒ toast + open Quick Quote deep-link in a new tab.
//
// Safety:
//   - Health / anthropometric data is NEVER sent by this button. That flow
//     lives in the IntegrityHealthPanel (PR-6) with an explicit confirm
//     dialog, per the non-negotiable rule that AI-extracted health data
//     never leaves CIVIX without agent approval.
//   - The button is disabled entirely when there are no credentials for
//     this tenant (admin needs to add one first).

import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Loader2, Send, ExternalLink, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../../lib/api.js';

// Default to Final Expense — highest-volume product in the DG book and the
// only single-step flow. Agents can pick a different Life product from the
// dropdown for anything more complex.
const DEFAULT_PRODUCT = 'finalExpense';

// The `callId` prop is optional. When present we offer the agent a checkbox
// to embed the call's AI-extracted health-notes block into Integrity's
// free-text lead notes on push. This is compliant with the "never auto-
// submit AI-extracted health data" rule because the notes field is a
// human-readable stenographer view — the Integrity agent still manually
// selects every structured Health Profile value themselves.
export default function SendToIntegrityButton({ leadId, lead, callId = null }) {
  const qc = useQueryClient();
  const [product, setProduct] = useState(DEFAULT_PRODUCT);
  const [showMenu, setShowMenu] = useState(false);
  // Default ON when a call is available. Toggling reverts to a notes-free
  // push in a single click, no confirmation dialog needed — the block is
  // additive and the agent can always re-push later with it included.
  const [includeHealthNotes, setIncludeHealthNotes] = useState(true);
  // Intake block (identity + address + beneficiary + income + expenses) is
  // additive too and defaults ON when a call context resolves. The renderer
  // no-ops when no ClientIntake exists for the call, so leaving this ON on
  // a call that never produced intake facts is safe — nothing is written.
  const [includeIntakeNotes, setIncludeIntakeNotes] = useState(true);

  // Lightweight loads. React-Query dedupes so this cost is one call each per
  // detail-page open, then cached for 5 min.
  const credsQ = useQuery({
    queryKey: ['integrity-credentials'],
    queryFn: () => apiGet('/integrity/credentials'),
    staleTime: 5 * 60_000,
  });
  const productsQ = useQuery({
    queryKey: ['integrity-products'],
    queryFn: () => apiGet('/integrity/products'),
    staleTime: 10 * 60_000,
  });
  const linksQ = useQuery({
    queryKey: ['integrity-links', leadId],
    queryFn: () => apiGet(`/integrity/leads/${leadId}/link`),
    enabled: !!leadId,
  });

  // Discover a call context for the health-notes toggle. We reuse the same
  // /health/current endpoint that IntegrityHealthPanel loads, so no extra
  // service work is needed — React-Query dedupes and both components read
  // the same cached row. If no extraction exists, resolvedCallId stays null
  // and the checkbox is not rendered.
  const extractionQ = useQuery({
    queryKey: ['integrity-health-current', leadId],
    queryFn: () => apiGet(`/integrity/leads/${leadId}/health/current`),
    enabled: !!leadId,
    staleTime: 30_000,
  });
  const resolvedCallId = callId || extractionQ.data?.extraction?.call || null;

  const credentials = credsQ.data?.credentials || [];
  const products = productsQ.data?.products || [];
  const existingLinks = linksQ.data?.links || [];

  // Pick the first non-disabled credential automatically. In the (rare)
  // multi-NPN case, we'd render a chooser — deferred until a real second
  // NPN lands. Once Ryan's NPN is in, this becomes a dropdown.
  const activeCred = useMemo(
    () => credentials.find((c) => !c.disabled) || null,
    [credentials],
  );

  const pushMut = useMutation({
    mutationFn: () => apiPost(`/integrity/leads/${leadId}/push`, {
      npn: activeCred?.npn,
      product,
      // Only send callId + toggle when a callId is actually in scope, so an
      // agent pushing from a lead page with no call context doesn't confuse
      // the backend with a null callId + include=true.
      ...(resolvedCallId ? { callId: resolvedCallId, includeHealthNotes, includeIntakeNotes } : {}),
    }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['integrity-links', leadId] });
      const link = data?.link;
      const quote = data?.quoteLink;
      if (link?.pushStatus === 'pushed') {
        toast.success(`Sent to Integrity (leadsId ${link.integrityLeadId})`);
        if (quote?.url && quote?.verified) {
          window.open(quote.url, '_blank', 'noopener,noreferrer');
        }
      } else if (data?.error) {
        toast.error(`Integrity push failed (${data.error.status}): ${data.error.message}`);
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Could not push lead to Integrity');
    },
  });

  const loading = credsQ.isLoading || productsQ.isLoading || linksQ.isLoading;

  if (loading) {
    return (
      <button className="btn-ghost w-full text-sm flex items-center justify-center gap-2 opacity-70" disabled>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Integrity…
      </button>
    );
  }

  // No credentials configured — surface a helpful message rather than a
  // dead button. Admin must add one via Settings ▸ Integrations first.
  if (!activeCred) {
    return (
      <div className="rounded-lg bg-civix-panel border border-civix-border p-3 text-[11px] text-white/55">
        <div className="font-semibold text-white/85 mb-1 flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5 text-civix-purpleSoft" /> Integrity Connect
        </div>
        Ask an admin to add an Integrity Connect credential in Settings ▸ Integrations to enable partner-lead push.
      </div>
    );
  }

  // Already pushed under any NPN? Show a "Open in Integrity" button rather
  // than re-push, and expose a small re-push affordance for the same NPN.
  const linkedForActiveNpn = existingLinks.find((l) => l.npn === activeCred.npn);
  if (linkedForActiveNpn && linkedForActiveNpn.pushStatus === 'pushed') {
    const quote = linkedForActiveNpn.quoteLink;
    return (
      <div className="rounded-lg bg-civix-panel border border-civix-border p-3 space-y-2">
        <div className="text-xs font-semibold text-white/85 flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5 text-emerald-400" /> Sent to Integrity
        </div>
        <div className="text-[11px] text-white/55">
          leadsId <span className="text-white/80">{linkedForActiveNpn.integrityLeadId}</span>
          {linkedForActiveNpn.remoteStatus ? <> · {linkedForActiveNpn.remoteStatus}</> : null}
        </div>
        {quote?.url ? (
          <a
            href={quote.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
          >
            <ExternalLink className="w-4 h-4" /> Open Quick Quote
          </a>
        ) : null}
        <button
          type="button"
          onClick={() => pushMut.mutate()}
          disabled={pushMut.isPending}
          className="btn-ghost w-full text-[11px] flex items-center justify-center gap-1"
        >
          {pushMut.isPending ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Re-syncing…</>
          ) : (
            <>Re-sync with Integrity</>
          )}
        </button>
      </div>
    );
  }

  const chosenProduct = products.find((p) => p.key === product) || products[0];

  return (
    <div className="rounded-lg bg-civix-panel border border-civix-border p-3 space-y-2">
      <div className="text-xs font-semibold text-white/85 flex items-center gap-1.5">
        <Send className="w-3.5 h-3.5 text-civix-purpleSoft" /> Send to Integrity
      </div>

      {/* Product picker. Unverified slugs render a warning strip so we don't
          silently open a broken URL. */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowMenu((v) => !v)}
          className="w-full text-left rounded-sm bg-civix-bg border border-civix-border px-2 py-1.5 text-[11px] flex items-center justify-between hover:border-white/20"
        >
          <span className="text-white/85">{chosenProduct?.label || product}</span>
          <ChevronDown className="w-3 h-3 text-white/45" />
        </button>
        {showMenu && (
          <div className="absolute z-30 mt-1 w-full rounded-sm bg-civix-panel border border-civix-border shadow-xl">
            {products.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => { setProduct(p.key); setShowMenu(false); }}
                className={`block w-full text-left px-2 py-1.5 text-[11px] hover:bg-civix-bg ${p.key === product ? 'text-white' : 'text-white/70'}`}
              >
                {p.label}
                {p.verified === false && (
                  <span className="ml-1 text-[10px] text-amber-400">(unverified URL)</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {chosenProduct && chosenProduct.verified === false && (
        <div className="text-[10px] text-amber-300/85">
          Product URL not yet verified — Quick Quote will open the Integrity clients list.
        </div>
      )}

      {resolvedCallId ? (
        <div className="space-y-1">
          <label className="flex items-start gap-2 rounded-sm bg-civix-bg border border-civix-border px-2 py-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={includeIntakeNotes}
              onChange={(e) => setIncludeIntakeNotes(e.target.checked)}
              className="mt-0.5 accent-civix-red"
            />
            <span className="text-[11px] text-white/80 leading-snug">
              Include intake notes from this call in Integrity lead notes
              <span className="block text-[10px] text-white/45">
                Full name, DOB, address, beneficiary, income and expenses. Integrity agent still finalizes structured fields.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded-sm bg-civix-bg border border-civix-border px-2 py-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={includeHealthNotes}
              onChange={(e) => setIncludeHealthNotes(e.target.checked)}
              className="mt-0.5 accent-civix-red"
            />
            <span className="text-[11px] text-white/80 leading-snug">
              Include health notes from this call in Integrity lead notes
              <span className="block text-[10px] text-white/45">
                Bullet-point summary of what the client disclosed. Integrity agent still selects structured fields manually.
              </span>
            </span>
          </label>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => pushMut.mutate()}
        disabled={pushMut.isPending || !lead || !activeCred}
        className="btn-primary w-full flex items-center justify-center gap-2 text-sm disabled:opacity-50"
      >
        {pushMut.isPending ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
        ) : (
          <><ArrowUpRight className="w-4 h-4" /> Send (NPN {activeCred.npn})</>
        )}
      </button>

      <div className="text-[10px] text-white/40 leading-tight">
        Sends name, contact, and address{resolvedCallId && (includeIntakeNotes || includeHealthNotes) ? `, plus the call’s ${[includeIntakeNotes && 'intake', includeHealthNotes && 'health'].filter(Boolean).join(' and ')} notes as free-text` : ''}. Structured Integrity fields never leave CIVIX from this button.
      </div>
    </div>
  );
}
